import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { 
  Wallet, 
  CreditCard, 
  Plus, 
  Eye, 
  ExternalLink, 
  Download,
  Clock,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Transaction } from '@solana/web3.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { format } from 'date-fns';
import { PaymentPreview, TxProgressList } from '../components/payments/PaymentBatch';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Validate Solana wallet address format
const isValidSolanaAddress = (address) => {
  if (!address) return false;
  const base58Pattern = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  return base58Pattern.test(address);
};

export default function Payments() {
  const { user } = useAuth();
  
  const [payments, setPayments] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  
  const isAdmin = user?.role === 'admin';

  // Check if Phantom is installed and connected
  const checkWalletConnection = useCallback(async () => {
    const phantom = window.solana;
    if (phantom?.isPhantom) {
      try {
        if (phantom.isConnected) {
          setWalletConnected(true);
          setWalletAddress(phantom.publicKey?.toString());
        }
      } catch (error) {
        console.error('Error checking wallet:', error);
      }
    }
  }, []);

  useEffect(() => {
    checkWalletConnection();
    
    const phantom = window.solana;
    if (phantom?.isPhantom) {
      phantom.on('connect', () => {
        setWalletConnected(true);
        setWalletAddress(phantom.publicKey?.toString());
      });
      phantom.on('disconnect', () => {
        setWalletConnected(false);
        setWalletAddress(null);
      });
    }
  }, [checkWalletConnection]);

  const connectWallet = async () => {
    const phantom = window.solana;
    if (!phantom?.isPhantom) {
      toast.error('Phantom wallet not found. Please install it from phantom.app');
      window.open('https://phantom.app/', '_blank');
      return;
    }
    
    try {
      const response = await phantom.connect();
      setWalletConnected(true);
      setWalletAddress(response.publicKey.toString());
      toast.success('Wallet connected');
    } catch (error) {
      console.error('Error connecting wallet:', error);
      toast.error('Failed to connect wallet');
    }
  };

  const disconnectWallet = async () => {
    const phantom = window.solana;
    if (phantom) {
      await phantom.disconnect();
      setWalletConnected(false);
      setWalletAddress(null);
      toast.success('Wallet disconnected');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/payments`),
        axios.get(`${API_URL}/invoices?status=open`)
      ]);
      setPayments(paymentsRes.data);
      setOpenInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleInvoiceSelection = (invoiceId) => {
    const invoice = openInvoices.find(inv => inv.invoice_id === invoiceId);
    if (invoice && isValidSolanaAddress(invoice.va_wallet_address)) {
      setSelectedInvoices(prev => 
        prev.includes(invoiceId) 
          ? prev.filter(id => id !== invoiceId)
          : [...prev, invoiceId]
      );
    }
  };

  const selectAllInvoices = () => {
    const validInvoices = openInvoices.filter(inv => isValidSolanaAddress(inv.va_wallet_address));
    if (selectedInvoices.length === validInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(validInvoices.map(inv => inv.invoice_id));
    }
  };

  const getSelectedTotal = () => {
    return openInvoices
      .filter(inv => selectedInvoices.includes(inv.invoice_id))
      .reduce((sum, inv) => sum + inv.amount, 0);
  };

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [txProgress, setTxProgress] = useState([]);

  useEffect(() => {
    if (!showCreateDialog || !walletConnected || !walletAddress || selectedInvoices.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(`${API_URL}/payments/preview`, {
          sender_wallet: walletAddress,
          invoice_ids: selectedInvoices
        });
        if (!cancelled) { setPreview(res.data); setPreviewError(null); }
      } catch (error) {
        if (!cancelled) { setPreview(null); setPreviewError(error.response?.data?.detail || 'Preview failed'); }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showCreateDialog, walletConnected, walletAddress, selectedInvoices]);

  const updateTx = (index, patch) =>
    setTxProgress(prev => prev.map(t => (t.index === index ? { ...t, ...patch } : t)));

  const pollConfirmations = async (sent, lastValidBlockHeight) => {
    let pending = sent;
    const deadline = Date.now() + 120000;
    while (pending.length > 0 && Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await axios.post(`${API_URL}/payments/confirm`, {
          sender_wallet: walletAddress,
          last_valid_block_height: lastValidBlockHeight,
          items: pending.map(p => ({ signature: p.signature, invoice_ids: p.invoice_ids }))
        });
        res.data.results.forEach((r, i) => {
          if (r.status !== 'pending') updateTx(pending[i].index, { status: r.status, error: r.error });
        });
        pending = pending.filter((_, i) => res.data.results[i].status === 'pending');
      } catch (error) {
        console.error('Confirm polling error:', error);
      }
    }
    pending.forEach(p => updateTx(p.index, { status: 'pending', error: 'Still unconfirmed - check Solscan' }));
  };

  const executePayment = async () => {
    const phantom = window.solana;
    if (!phantom?.isPhantom || !walletConnected) {
      toast.error('Please connect your Phantom wallet first');
      return;
    }
    if (selectedInvoices.length === 0) {
      toast.error('No invoices selected');
      return;
    }

    setProcessing(true);
    setTxProgress([]);
    try {
      toast.info('Preparing transactions...');
      const { data: plan } = await axios.post(`${API_URL}/payments/prepare`, {
        sender_wallet: walletAddress,
        invoice_ids: selectedInvoices
      });

      setTxProgress(plan.transactions.map(t => ({
        index: t.index, names: t.names, amount: t.amount, transfer_count: t.transfer_count,
        status: 'signing', signature: null, error: null
      })));

      toast.info(`Approve ${plan.tx_count} transaction(s) in Phantom (one click)...`);
      const unsigned = plan.transactions.map(t => Transaction.from(Buffer.from(t.transaction, 'base64')));
      const signed = await phantom.signAllTransactions(unsigned);

      const { data: submit } = await axios.post(`${API_URL}/payments/submit`, {
        transactions: signed.map((tx, i) => ({
          signed_transaction: Buffer.from(tx.serialize()).toString('base64'),
          invoice_ids: plan.transactions[i].invoice_ids
        }))
      });

      submit.results.forEach(r => updateTx(r.index, r.signature
        ? { status: 'sent', signature: r.signature }
        : { status: 'failed', error: r.error }));

      const sent = submit.results.filter(r => r.signature);
      if (sent.length > 0) {
        toast.info('Transactions sent, waiting for confirmation...');
        await pollConfirmations(sent, plan.last_valid_block_height);
      }

      toast.success('Batch finished - see the status of each transaction');
      setSelectedInvoices([]);
      fetchData();
    } catch (error) {
      console.error('Payment error:', error);
      setTxProgress(prev => prev.map(t => (t.status === 'signing' ? { ...t, status: 'cancelled' } : t)));
      if (error.message?.includes('User rejected') || error.code === 4001) {
        toast.error('Transaction cancelled by user');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(error.message || 'Payment failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  const viewPaymentDetails = async (payment) => {
    setSelectedPayment(payment);
    setShowDetailDialog(true);
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.invoice_id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.reference || invoice.invoice_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + p.total_amount, 0);
  const totalPending = openInvoices.reduce((sum, inv) => sum + inv.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        {isAdmin && (
          <div className="flex items-center gap-4">
            {walletConnected ? (
              <Button 
                onClick={disconnectWallet}
                variant="outline"
                className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
              >
                <Wallet className="w-4 h-4 mr-2" />
                {walletAddress?.substring(0, 4)}...{walletAddress?.substring(walletAddress.length - 4)}
              </Button>
            ) : (
              <Button 
                onClick={connectWallet}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Connect Phantom
              </Button>
            )}
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-white text-black hover:bg-zinc-200"
              disabled={!walletConnected || openInvoices.length === 0}
              data-testid="new-payment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Payment
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card p-4 rounded-md border border-border">
          <div className="text-sm text-zinc-400 mb-1">Total Payments</div>
          <div className="text-2xl font-bold text-white">{payments.length}</div>
        </div>
        <div className="bg-card p-4 rounded-md border border-green-500/20">
          <div className="text-sm text-zinc-400 mb-1">Total Paid</div>
          <div className="text-2xl font-bold text-green-500">${totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-card p-4 rounded-md border border-amber-500/20">
          <div className="text-sm text-zinc-400 mb-1">Pending</div>
          <div className="text-2xl font-bold text-amber-500">${totalPending.toFixed(2)}</div>
        </div>
      </div>

      {/* Wallet Connection Status */}
      {isAdmin && !walletConnected && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-4">
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-amber-500" />
            <div>
              <div className="text-amber-500 font-medium">Wallet not connected</div>
              <div className="text-sm text-zinc-400">Connect your Phantom wallet to make USDC payments</div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Payment History</h2>
        
        {payments.length === 0 ? (
          <div className="bg-card rounded-md border border-border p-8 text-center">
            <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <div className="text-zinc-400">No payments yet</div>
          </div>
        ) : (
          <div className="bg-card rounded-md border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Transaction ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Invoices</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow
                    key={payment.payment_id}
                    className="border-b border-border table-row-hover cursor-pointer"
                    onClick={() => viewPaymentDetails(payment)}
                    data-testid={`payment-row-${payment.payment_id}`}
                  >
                    <TableCell className="font-mono text-sm text-white">
                      {payment.transaction_hash 
                        ? `${payment.transaction_hash.substring(0, 8)}...${payment.transaction_hash.substring(payment.transaction_hash.length - 8)}`
                        : payment.payment_id.substring(0, 12)}
                    </TableCell>
                    <TableCell className="text-zinc-300">
                      {format(new Date(payment.created_at), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-zinc-300">
                      {payment.invoice_ids?.length || 0} invoice(s)
                    </TableCell>
                    <TableCell className="text-white font-semibold">
                      ${payment.total_amount?.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <span className="bg-green-500/10 text-green-500 border border-green-500/20 px-2 py-0.5 rounded text-xs font-medium">
                        COMPLETED
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewPaymentDetails(payment);
                          }}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {payment.transaction_hash && !payment.transaction_hash.startsWith('MANUAL_') && !payment.transaction_hash.startsWith('SIMULATION_') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://solscan.io/tx/${payment.transaction_hash}`, '_blank');
                            }}
                            className="text-blue-500 hover:text-blue-400"
                            title="View on Solscan"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create Payment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              New Payment
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select invoices to pay via USDC on Solana
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Wallet Status */}
            <div className="bg-zinc-800/50 rounded-md p-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-zinc-400">Connected Wallet</div>
                <div className="text-white font-mono text-sm">
                  {walletAddress ? `${walletAddress.substring(0, 8)}...${walletAddress.substring(walletAddress.length - 8)}` : 'Not connected'}
                </div>
              </div>
              {walletConnected ? (
                <Button 
                  onClick={disconnectWallet}
                  variant="outline"
                  size="sm"
                  className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                >
                  Disconnect
                </Button>
              ) : (
                <Button 
                  onClick={connectWallet}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Connect
                </Button>
              )}
            </div>

            {/* Info about USDC */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-400">
                Payments are made in USDC on Solana Mainnet. Ensure your wallet has sufficient USDC balance and SOL for transaction fees.
              </div>
            </div>

            {/* Invoice Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-400">Select invoices to pay</div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllInvoices}
                  className="text-zinc-400 hover:text-white"
                >
                  {selectedInvoices.length === openInvoices.filter(inv => isValidSolanaAddress(inv.va_wallet_address)).length 
                    ? 'Deselect All' 
                    : 'Select All'}
                </Button>
              </div>
              
              <div className="bg-zinc-800/30 rounded-md border border-zinc-800 max-h-64 overflow-y-auto">
                {openInvoices.length === 0 ? (
                  <div className="p-4 text-center text-zinc-500">No open invoices</div>
                ) : (
                  openInvoices.map((invoice) => {
                    const hasValidWallet = isValidSolanaAddress(invoice.va_wallet_address);
                    const walletStatus = !invoice.va_wallet_address 
                      ? 'No wallet' 
                      : !hasValidWallet 
                        ? 'Invalid wallet format' 
                        : null;
                    
                    return (
                      <div 
                        key={invoice.invoice_id}
                        className={`flex items-center justify-between p-3 border-b border-zinc-800 last:border-b-0 ${
                          !hasValidWallet ? 'opacity-50 bg-red-500/5' : 'hover:bg-zinc-800/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedInvoices.includes(invoice.invoice_id)}
                            onCheckedChange={() => toggleInvoiceSelection(invoice.invoice_id)}
                            disabled={!hasValidWallet}
                            data-testid={`select-invoice-${invoice.invoice_id}`}
                          />
                          <div>
                            <div className="text-white font-medium">{invoice.va_name}</div>
                            <div className="text-xs text-zinc-500">{invoice.reference}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-semibold">${invoice.amount?.toFixed(2)}</div>
                          {walletStatus && (
                            <div className="text-xs text-red-500">{walletStatus}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Summary */}
            {selectedInvoices.length > 0 && (
              <div className="bg-zinc-800/50 rounded-md p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Selected Invoices</span>
                  <span className="text-white">{selectedInvoices.length}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-zinc-700 pt-2">
                  <span className="text-white">Total Amount</span>
                  <span className="text-green-500">${getSelectedTotal().toFixed(2)} USDC</span>
                </div>
              </div>
            )}

            <PaymentPreview preview={preview} loading={previewLoading} error={previewError} />
            <TxProgressList items={txProgress} />

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={executePayment}
                disabled={!walletConnected || selectedInvoices.length === 0 || processing || previewLoading || (preview && (!preview.sufficient_usdc || !preview.sufficient_sol))}
                className="flex-1 bg-green-600 text-white hover:bg-green-700"
                data-testid="execute-payment-btn"
              >
                {processing ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Pay ${getSelectedTotal().toFixed(2)} USDC{preview ? ` (${preview.tx_count} tx)` : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Payment Details</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedPayment?.reference || `Payment ${selectedPayment?.payment_id?.substring(0, 8)}`}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4">
              {/* Payment Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Date</div>
                  <div className="text-white">
                    {format(new Date(selectedPayment.created_at), 'MMM dd, yyyy HH:mm')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Total Amount</div>
                  <div className="text-green-500 font-bold text-lg">
                    ${selectedPayment.total_amount?.toFixed(2)} USDC
                  </div>
                </div>
              </div>

              {/* Transaction Hash */}
              {selectedPayment.transaction_hash && (
                <div className="bg-zinc-800/50 rounded-md p-3">
                  <div className="text-xs text-zinc-500 uppercase mb-1">Transaction Hash</div>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-zinc-300 flex-1 break-all">
                      {selectedPayment.transaction_hash}
                    </code>
                    {!selectedPayment.transaction_hash.startsWith('MANUAL_') && !selectedPayment.transaction_hash.startsWith('SIMULATION_') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`https://solscan.io/tx/${selectedPayment.transaction_hash}`, '_blank')}
                        className="text-blue-500 hover:text-blue-400 shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {!selectedPayment.transaction_hash.startsWith('MANUAL_') && !selectedPayment.transaction_hash.startsWith('SIMULATION_') && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://solscan.io/tx/${selectedPayment.transaction_hash}`, '_blank')}
                        className="text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        View on Solscan
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`https://orbmarkets.io/tx/${selectedPayment.transaction_hash}`, '_blank')}
                        className="text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        View on Orbmarkets
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Paid Invoices */}
              <div>
                <div className="text-sm text-zinc-400 mb-2">Paid Invoices ({selectedPayment.invoices?.length || 0})</div>
                <div className="bg-zinc-800/30 rounded-md border border-zinc-800">
                  {selectedPayment.invoices?.map((invoice, index) => (
                    <div 
                      key={invoice.invoice_id || index}
                      className="flex items-center justify-between p-3 border-b border-zinc-800 last:border-b-0"
                    >
                      <div>
                        <div className="text-white font-medium">{invoice.va_name}</div>
                        <div className="text-xs text-zinc-500">{invoice.reference}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-white font-semibold">${invoice.amount?.toFixed(2)}</div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadInvoice(invoice)}
                            className="text-blue-500 hover:text-blue-400"
                            title="Download Invoice"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
